// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// Swept narrow phase — the anti-tunnelling guard (M1-23, PH-A14).
//
// The contact *response* is discrete and stays that way: it is calibrated, it is stable,
// and every real pinball simulator resolves contacts discretely. What a discrete test
// cannot do is notice a collider the ball jumped clean over between one step and the next.
// At the 8 m/s clamp a ball travels 8 mm per step against a 13.49 mm radius, so a wall is
// safe — but a wire habitrail is a great deal thinner than a wall, and a drop target
// dropping into a ball's path can put geometry where the ball has already been.
//
// So: solve analytically for the *earliest* moment on this step at which the ball first
// touches anything, and if that moment is before the end of the step, place the ball just
// past that first touch and let the discrete solver respond from there. The ball forfeits
// the remainder of its travel for that one step — at most 8 mm, and only on a step that
// would otherwise have tunnelled, where the alternative is losing the ball entirely.
//
// Every routine here uses only `+ − × ÷ √`, so it is bit-identical across runtimes
// (PH-081).

import type { ArcCollider, CircleCollider, Collider, SegmentCollider } from './colliders'
import { KIND_ARC, KIND_CIRCLE, KIND_SEGMENT } from './colliders'

/** Returned when nothing is struck during the step. Any value above 1 reads as "not this step". */
export const NO_HIT = 2

/**
 * Smallest root of `a·t² + b·t + c = 0` lying in `[0, 1]`, or `NO_HIT`.
 *
 * `c < 0` means the ball already overlaps at the start of the step. That is not a
 * tunnelling case — the discrete solver can see it perfectly well — so it is deliberately
 * reported as a miss rather than resolved here.
 */
function firstRootInStep(a: number, b: number, c: number): number {
  if (c < 0) return NO_HIT
  if (a <= 0) return NO_HIT
  const disc = b * b - 4 * a * c
  if (disc < 0) return NO_HIT
  const t = (-b - Math.sqrt(disc)) / (2 * a)
  return t >= 0 && t <= 1 ? t : NO_HIT
}

/**
 * Time of impact against a segment, treating the ball as a moving disc of radius `r`.
 *
 * Two families of contact: the flat face, solved linearly against the segment's own
 * normal, and the two round endpoint caps, each a quadratic. The earliest wins.
 */
export function sweptSegmentTOI(
  s: SegmentCollider,
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  r: number,
): number {
  const dx = x1 - x0
  const dy = y1 - y0

  const ex = s.bx - s.ax
  const ey = s.by - s.ay
  const len2 = ex * ex + ey * ey

  let best = NO_HIT

  // --- the flat face -------------------------------------------------------
  // Signed distance from the face, along its own normal, is linear in t.
  const d0 = (x0 - s.ax) * s.nx + (y0 - s.ay) * s.ny
  const d1 = (x1 - s.ax) * s.nx + (y1 - s.ay) * s.ny
  const dd = d1 - d0

  if (dd !== 0) {
    let tFace = NO_HIT
    if (d0 >= r && d1 < r) {
      tFace = (d0 - r) / -dd
    } else if (!s.oneSided && d0 <= -r && d1 > -r) {
      tFace = (-r - d0) / dd
    }
    if (tFace >= 0 && tFace <= 1) {
      // The face is finite: the contact only counts if it lands between the endpoints.
      const px = x0 + dx * tFace
      const py = y0 + dy * tFace
      const u = ((px - s.ax) * ex + (py - s.ay) * ey) / len2
      if (u >= 0 && u <= 1) best = tFace
    }
  }

  // --- the endpoint caps ---------------------------------------------------
  // A one-sided face still has to stop a ball that clips its cap from the live side;
  // a ball behind the face passes, which is what makes a gate a gate.
  const a = dx * dx + dy * dy
  if (a > 0) {
    for (let end = 0; end < 2; end++) {
      const cx = end === 0 ? s.ax : s.bx
      const cy = end === 0 ? s.ay : s.by
      if (s.oneSided) {
        const behind = (x0 - cx) * s.nx + (y0 - cy) * s.ny
        if (behind < 0) continue
      }
      const qx = x0 - cx
      const qy = y0 - cy
      const t = firstRootInStep(a, 2 * (qx * dx + qy * dy), qx * qx + qy * qy - r * r)
      if (t < best) best = t
    }
  }

  return best
}

/** Time of impact against a circular obstacle — a post, a bumper skirt, a captive ball. */
export function sweptCircleTOI(
  c: CircleCollider,
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  r: number,
): number {
  const dx = x1 - x0
  const dy = y1 - y0
  const qx = x0 - c.cx
  const qy = y0 - c.cy
  const sum = c.radius + r
  return firstRootInStep(
    dx * dx + dy * dy,
    2 * (qx * dx + qy * dy),
    qx * qx + qy * qy - sum * sum,
  )
}

/** Angular containment of a point about an arc's centre, by the same dot product the
 *  discrete query uses — no `atan2` here either. */
function withinArcSpan(a: ArcCollider, px: number, py: number): boolean {
  const ux = px - a.cx
  const uy = py - a.cy
  const d2 = ux * ux + uy * uy
  if (d2 < 1e-24) return false
  const inv = 1 / Math.sqrt(d2)
  return (ux * inv) * a.midX + (uy * inv) * a.midY >= a.cosHalfSpan
}

/**
 * Time of impact against an arc.
 *
 * A *containing* arc (`insideOut === false`) is a wall the ball is inside, so it is struck
 * on the way **out** — the later root of the quadratic, not the earlier one. Getting that
 * backwards makes a playfield shoulder silently permeable, which is exactly the class of
 * bug this guard exists to catch.
 */
export function sweptArcTOI(
  a: ArcCollider,
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  r: number,
): number {
  const dx = x1 - x0
  const dy = y1 - y0
  const qa = dx * dx + dy * dy
  if (qa <= 0) return NO_HIT

  const qx = x0 - a.cx
  const qy = y0 - a.cy
  const qb = 2 * (qx * dx + qy * dy)

  let t: number
  if (a.insideOut) {
    // Convex obstacle: identical to a circle.
    const sum = a.radius + r
    t = firstRootInStep(qa, qb, qx * qx + qy * qy - sum * sum)
  } else {
    const shell = a.radius - r
    const qc = qx * qx + qy * qy - shell * shell
    if (qc > 0) return NO_HIT // already at or beyond the wall; the discrete test owns this
    const disc = qb * qb - 4 * qa * qc
    if (disc < 0) return NO_HIT
    t = (-qb + Math.sqrt(disc)) / (2 * qa)
    if (t < 0 || t > 1) return NO_HIT
  }

  if (t === NO_HIT) return NO_HIT
  return withinArcSpan(a, x0 + dx * t, y0 + dy * t) ? t : NO_HIT
}

/** Dispatch. Mirrors `colliderContact`, and is exhaustive for the same reason. */
export function sweptColliderTOI(
  c: Collider,
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  r: number,
): number {
  r+=c.padding??0
  switch (c.kind) {
    case KIND_SEGMENT:
      return sweptSegmentTOI(c, x0, y0, x1, y1, r)
    case KIND_ARC:
      return sweptArcTOI(c, x0, y0, x1, y1, r)
    case KIND_CIRCLE:
      return sweptCircleTOI(c, x0, y0, x1, y1, r)
    default: {
      const never: never = c
      throw new Error(`sweptColliderTOI: unhandled collider ${String(never)}`)
    }
  }
}
